'use client';

import { MessageSquare } from 'lucide-react';
import Link from 'next/link';
import { useDmThreads, useStudioDmThreads } from '@/api/queries/dm.queries';
import { SidebarMenuButton, SidebarMenuItem } from '@/components/ui/sidebar';
import { useChromePathname } from '@/shared/components/providers/chrome-pathname';
import { canSeePaidDms } from '@/shared/lib/auth/permissions';
import { useAuthStore } from '@/shared/stores/auth-store';

/**
 * TEMP tester-gated "Messages" item inside the You group (FRONT-311 —
 * canvas order: Watchlist · Messages · Invite · Creator Studio). Renders null
 * for non-testers, so the neutral static shell never contains it; active
 * state and unread badge arrive at hydration. Remove the gate with
 * canSeePaidDms at full release.
 *
 * Creators land in the studio inbox (operator decision 2026-08-12) — one
 * inbox per role, so the target and the badge are destination-scoped:
 * creators see studio unread, everyone else subscriber unread. A creator's
 * own fan-side threads stay reachable via /messages (profile send flow),
 * just without a sidebar entry.
 */
export function NavMessagesItem() {
  const pathname = useChromePathname();
  const user = useAuthStore((s) => s.user);
  const isCreator = !!user?.is_creator;
  const subscriber = useDmThreads({ enabled: !isCreator });
  const studio = useStudioDmThreads({ enabled: isCreator });

  if (!canSeePaidDms(user)) return null;

  const href = isCreator ? '/studio/messages' : '/messages';
  const threads = (isCreator ? studio.data?.threads : subscriber.data?.threads) ?? [];
  const unread = threads.filter((t) => t.unread_count > 0).length;
  const isActive = pathname !== null && (pathname === href || pathname.startsWith(`${href}/`));

  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild isActive={isActive} tooltip="Messages">
        <Link href={href} className="relative">
          <MessageSquare />
          <span>Messages</span>
          {unread > 0 && (
            <span className="ml-auto flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red-primary px-1 text-[10.5px] font-bold text-white group-data-[collapsible=icon]:hidden">
              {unread > 99 ? '99+' : unread}
            </span>
          )}
          {/* Collapsed rail clips flow children (size-8 + overflow-hidden) —
              the icon mode gets an absolutely-positioned dot instead. */}
          {unread > 0 && (
            <span className="absolute right-1 top-1 hidden size-2 rounded-full bg-red-primary group-data-[collapsible=icon]:block" />
          )}
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}
